/**
 * PHARMAKON — Fournisseur ERP · legacy section adapter (Module 00, §3.5.1)
 *
 * Renders an existing monolith section (client/src/pages/fournisseur/sections/*)
 * inside the new modular layout, so Catalogue / Négociations / Clients keep
 * working until they are migrated to ERP V3 in their own modules. The legacy
 * Pré-commandes section (its own offer form on the retired intake) is unmounted:
 * pré-commandes and offers live in the Commercial workspace (X §11.2, XWP-29).
 *
 * Bridges the legacy `showToast` / `onBadgeChange` props to the real toast
 * infra, and lazily injects the legacy PORTAL_CSS exactly once (the only place
 * the monolith styling survives — to be deleted per-domain as each migrates).
 *
 * This adapter is intentionally minimal and clearly marked TEMPORARY.
 */
import * as React from "react";
import { useToast } from "@/hooks/useToast.js";
import { PORTAL_CSS } from "@/pages/fournisseur/PortalUI.jsx";

const PortalCatalogue = React.lazy(() => import("@/pages/fournisseur/sections/PortalCatalogue.jsx"));
const PortalNegociations = React.lazy(() => import("@/pages/fournisseur/sections/PortalNegociations.jsx"));
const PortalClients = React.lazy(() => import("@/pages/fournisseur/sections/PortalClients.jsx"));

const SECTIONS = {
  catalogue: PortalCatalogue,
  negociations: PortalNegociations,
  clients: PortalClients,
};

// Inject the legacy stylesheet once for the lifetime of the app.
function useLegacyPortalCss() {
  React.useEffect(() => {
    if (document.getElementById("fs-legacy-portal-css")) return;
    const tag = document.createElement("style");
    tag.id = "fs-legacy-portal-css";
    tag.textContent = PORTAL_CSS;
    document.head.appendChild(tag);
    // Intentionally NOT removed on unmount: navigating between legacy sections
    // would otherwise thrash the stylesheet. Removed wholesale when the last
    // legacy section is migrated.
  }, []);
}

export default function LegacySection({ name }) {
  useLegacyPortalCss();
  const toast = useToast();
  const Section = SECTIONS[name];

  const showToast = React.useCallback(
    (msg, type = "success") => {
      if (type === "error") toast.error(msg);
      else if (type === "info") toast.info(msg);
      else toast.success(msg);
    },
    [toast],
  );

  if (!Section) {
    return (
      <div className="p-6 text-sm text-text-muted">Section indisponible.</div>
    );
  }

  return (
    <div className="portal-content p-6">
      <React.Suspense
        fallback={
          <div className="flex items-center justify-center py-24 text-text-muted">
            <svg
              className="animate-spin"
              viewBox="0 0 24 24"
              width="24"
              height="24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 12a9 9 0 11-6.219-8.56" />
            </svg>
          </div>
        }
      >
        <Section showToast={showToast} onBadgeChange={() => {}} />
      </React.Suspense>
    </div>
  );
}
